import type { FC, FormEvent, KeyboardEvent, ReactNode } from 'react'
import type { PaymentMethod, PlanId, PublicPaymentSettings, PublicStudent, Viewer } from '@/types'
import type { PaymentErrors } from '@/components/payments/payment-panel'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { formatInr } from '@/lib/format'
import { ApiError, api, errorMessage } from '@/lib/api-client'
import { BrandLockup } from '@/components/generals/logo'
import { PaymentPanel } from '@/components/payments/payment-panel'

import {
    Field,
    PasswordInput,
    SelectInput,
    TextInput,
    passwordStrength
} from '@/components/generals/form'

import {
    ArrowLeft,
    ArrowRight,
    Check,
    CircleAlert,
    LoaderCircle,
    LockKeyhole,
    PartyPopper,
    ShieldCheck,
    X
} from 'lucide-react'

import {
    BRANCHES,
    YEARS,
    defaultPlanForYear,
    eligiblePlans,
    getPlan,
    isPlanEligible
} from '@/constants/campus'

interface CheckoutModalProps {
    open: boolean
    planId: PlanId
    viewer: Viewer | null
    onClose: () => void
    onPlanChange: (planId: PlanId) => void
    onRegistered: (student: PublicStudent) => void
}

type Step = 'details' | 'payment' | 'done'

interface Details {
    name: string
    rollNo: string
    yearNo: number
    branch: string
    email: string
    password: string
}

type DetailErrors = Partial<Record<keyof Details, string>>

const EMPTY_DETAILS: Details = { name: '', rollNo: '', yearNo: 1, branch: 'CSE', email: '', password: '' }

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const validateDetails = (details: Details): DetailErrors => {
    const errors: DetailErrors = {}

    if (details.name.trim().length < 2) errors.name = 'Enter your full name.'
    if (!/^[A-Za-z0-9/-]{4,40}$/.test(details.rollNo.trim())) errors.rollNo = 'Enter your GKV roll number, for example 23FET1042.'
    if (!EMAIL_PATTERN.test(details.email.trim())) errors.email = 'Enter a valid college email address.'
    if (details.password.length < 8) errors.password = 'Use at least 8 characters.'

    return errors
}

const validatePayment = (method: PaymentMethod, reference: string, confirmed: boolean): PaymentErrors => {
    const errors: PaymentErrors = {}
    const value = reference.trim().toUpperCase()

    if (value.length < 6 || value.length > 40) errors.reference = 'Enter the reference from your payment (6 to 40 characters).'
    else if (!/^[A-Z0-9/-]+$/.test(value)) errors.reference = 'Use only letters, numbers, "/" and "-".'
    else if (method === 'UPI' && /^\d+$/.test(value) && value.length !== 12) errors.reference = 'A UPI UTR number has exactly 12 digits.'

    if (!confirmed) errors.confirmed = 'Please confirm you have completed the payment.'

    return errors
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export const CheckoutModal: FC<CheckoutModalProps> = ({ open, planId, viewer, onClose, onPlanChange, onRegistered }): ReactNode => {
    const router = useRouter()
    const dialogRef = useRef<HTMLDivElement>(null)
    const restoreFocus = useRef<HTMLElement | null>(null)
    const registered = useRef(false)

    const existing = viewer?.kind === 'student' && ['Unpaid', 'Rejected'].includes(viewer.student.accessStatus) ? viewer.student : null

    const [step, setStep] = useState<Step>('details')
    const [details, setDetails] = useState<Details>(EMPTY_DETAILS)
    const [detailErrors, setDetailErrors] = useState<DetailErrors>({})
    const [checking, setChecking] = useState(false)
    const [settings, setSettings] = useState<PublicPaymentSettings | null>(null)
    const [method, setMethod] = useState<PaymentMethod>('UPI')
    const [reference, setReference] = useState('')
    const [proof, setProof] = useState<File | null>(null)
    const [confirmed, setConfirmed] = useState(false)
    const [paymentErrors, setPaymentErrors] = useState<PaymentErrors>({})
    const [submitting, setSubmitting] = useState(false)
    const [formError, setFormError] = useState<string | null>(null)
    const [planNote, setPlanNote] = useState<string | null>(null)

    const plan = getPlan(planId)
    const yearNo = existing?.yearNo ?? details.yearNo
    const plans = eligiblePlans(yearNo)
    const strength = passwordStrength(details.password)

    // Fresh state every time the checkout opens.
    useEffect(() => {
        if (!open) return

        restoreFocus.current = document.activeElement as HTMLElement | null
        registered.current = false

        setStep(existing ? 'payment' : 'details')
        setDetails({ ...EMPTY_DETAILS, yearNo: getPlan(planId).year ?? 1 })
        setDetailErrors({})
        setMethod('UPI')
        setReference('')
        setProof(null)
        setConfirmed(false)
        setPaymentErrors({})
        setFormError(null)
        setPlanNote(null)
        setSubmitting(false)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    useEffect(() => {
        if (!open || settings) return

        api.publicPaymentSettings()
            .then(result => setSettings(result.settings))
            .catch(() => setFormError('We could not load the official payment details. Close this window and try again.'))
    }, [open, settings])

    useEffect(() => {
        if (!open) return

        const previous = document.documentElement.style.overflow

        document.documentElement.style.overflow = 'hidden'

        return () => {
            document.documentElement.style.overflow = previous
            restoreFocus.current?.focus?.()
        }
    }, [open])

    useEffect(() => {
        if (!open) return

        const timer = window.setTimeout(() => {
            const first = dialogRef.current?.querySelector<HTMLElement>('[data-autofocus]') ?? dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE)

            first?.focus()
        }, 80)

        return () => window.clearTimeout(timer)
    }, [open, step])

    // Keeps the chosen pass valid for the selected B.Tech year.
    useEffect(() => {
        if (!open || isPlanEligible(planId, yearNo)) return

        const next = defaultPlanForYear(yearNo)

        onPlanChange(next)
        setPlanNote(`Switched to the ${getPlan(next).fullName} to match Year ${yearNo}.`)
    }, [open, planId, yearNo, onPlanChange])

    const close = () => {
        if (submitting) return
        onClose()
    }

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
            event.stopPropagation()
            close()
            return
        }

        if (event.key !== 'Tab' || !dialogRef.current) return

        const nodes = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(node => node.offsetParent !== null)

        if (!nodes.length) return

        const first = nodes[0]
        const last = nodes[nodes.length - 1]

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault()
            last.focus()
        }
        else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault()
            first.focus()
        }
    }

    const update = <K extends keyof Details>(key: K, value: Details[K]) => {
        setDetails(current => ({ ...current, [key]: value }))
        setDetailErrors(current => ({ ...current, [key]: undefined }))
    }

    const continueToPayment = async (event: FormEvent) => {
        event.preventDefault()

        const errors = validateDetails(details)

        setDetailErrors(errors)

        if (Object.keys(errors).length) return

        setChecking(true)
        setFormError(null)

        try {
            const result = await api.checkStudent({ email: details.email.trim(), rollNo: details.rollNo.trim() })
            const taken: DetailErrors = {}

            if (result.emailTaken) taken.email = 'This email already has an account. Sign in instead.'
            if (result.rollTaken) taken.rollNo = 'This roll number is already registered.'

            if (Object.keys(taken).length) {
                setDetailErrors(taken)
                return
            }

            setStep('payment')
        }
        catch (error) {
            setFormError(errorMessage(error))
        }
        finally {
            setChecking(false)
        }
    }

    const completePayment = async (event: FormEvent) => {
        event.preventDefault()

        const errors = validatePayment(method, reference, confirmed)

        setPaymentErrors(errors)
        setFormError(null)

        if (Object.keys(errors).length) return

        setSubmitting(true)

        try {
            if (!existing && !registered.current) {
                const result = await api.registerStudent({
                    name: details.name.trim(),
                    email: details.email.trim(),
                    password: details.password,
                    rollNo: details.rollNo.trim(),
                    branch: details.branch,
                    yearNo: details.yearNo,
                    plan: planId
                })

                registered.current = true
                onRegistered(result.viewer.student)
            }

            let proofKey: string | null = null

            if (proof) {
                try {
                    proofKey = (await api.uploadProof(proof)).proofKey
                }
                catch (error) {
                    setPaymentErrors({ proof: `${errorMessage(error)} You can remove the screenshot and continue without it.` })
                    return
                }
            }

            await api.submitStudentAccessPayment({
                plan: planId,
                paymentMethod: method,
                paymentReference: reference.trim().toUpperCase(),
                proofKey,
                confirmed
            })

            setStep('done')
            window.setTimeout(() => router.push('/portal'), 1700)
        }
        catch (error) {
            if (error instanceof ApiError && error.status === 409 && !registered.current && !existing) {
                setStep('details')
                setDetailErrors(/roll number/i.test(error.message) ? { rollNo: error.message } : { email: error.message })
                return
            }

            setFormError(errorMessage(error))
        }
        finally {
            setSubmitting(false)
        }
    }

    const payer = existing ?? { name: details.name, rollNo: details.rollNo.toUpperCase() }

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    key='checkout'
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className='fixed inset-0 z-[90] flex items-end justify-center bg-midnight/75 backdrop-blur-sm sm:items-center sm:p-6'
                    onMouseDown={event => {
                        if (event.target === event.currentTarget) close()
                    }}
                >
                    <motion.div
                        ref={dialogRef}
                        role='dialog'
                        aria-modal='true'
                        aria-labelledby='checkout-title'
                        onKeyDown={onKeyDown}
                        initial={{ opacity: 0, y: 48, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 32, scale: 0.98 }}
                        transition={{ type: 'spring', stiffness: 320, damping: 32 }}
                        className='relative flex max-h-[100dvh] w-full max-w-5xl flex-col overflow-hidden rounded-t-[28px] bg-white shadow-[0_40px_120px_-30px_rgba(2,6,23,0.8)] sm:max-h-[94vh] sm:rounded-[28px] lg:flex-row'
                    >
                        <aside className='relative shrink-0 overflow-hidden bg-[linear-gradient(160deg,#101E2E,#08121E_60%)] p-5 text-white sm:p-6 lg:w-[350px] lg:p-8'>
                            <div aria-hidden='true' className='pointer-events-none absolute -right-20 -top-24 size-64 rounded-full bg-indigo-500/25 blur-3xl' />
                            <div aria-hidden='true' className='pointer-events-none absolute -bottom-24 -left-16 size-64 rounded-full bg-emerald-500/15 blur-3xl' />

                            <BrandLockup size={38} subtitle='Campus Pass checkout' className='relative' />

                            <div className='relative mt-5 rounded-2xl border border-white/10 bg-white/[0.05] p-4 lg:mt-8'>
                                <p className='text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-400'>
                                    {plan.label} · {plan.semesters}
                                </p>

                                <p className='mt-1.5 text-base font-semibold leading-snug'>{plan.name}</p>

                                <div className='mt-3 flex items-end gap-2'>
                                    <span className='text-3xl font-bold tracking-tight'>{formatInr(plan.price)}</span>
                                    <span className='pb-1 text-xs text-slate-400'>{plan.cadence}</span>
                                </div>
                            </div>

                            <ul className='relative mt-6 hidden space-y-3 lg:block'>
                                {plan.features.slice(0, 3).map(feature => (
                                    <li key={feature} className='flex gap-2.5 text-[13px] leading-relaxed text-slate-300'>
                                        <Check className='mt-0.5 size-4 shrink-0 text-emerald-400' strokeWidth={3} />
                                        {feature}
                                    </li>
                                ))}
                            </ul>

                            <ol className='relative mt-8 hidden space-y-4 border-t border-white/10 pt-6 lg:block'>
                                {[
                                    ["Student details", "Your account and roll number"],
                                    ["Payment", "UPI, bank transfer or campus receipt"],
                                    ["T&P verification", "Your portal unlocks once verified"]
                                ].map(([title, text], index) => {
                                    const reached = step === 'done' || (step === 'payment' ? index <= 1 : index === 0)

                                    return (
                                        <li key={title} className='flex gap-3'>
                                            <span className={cn('grid size-6 shrink-0 place-items-center rounded-full text-[11px] font-bold', reached ? 'bg-white text-ink' : 'bg-white/10 text-slate-400')}>
                                                {step === 'done' || (step === 'payment' && index === 0) ? <Check className='size-3.5' strokeWidth={3} /> : index + 1}
                                            </span>

                                            <span>
                                                <span className={cn('block text-sm font-semibold', reached ? 'text-white' : 'text-slate-400')}>{title}</span>
                                                <span className='block text-xs text-slate-500'>{text}</span>
                                            </span>
                                        </li>
                                    )
                                })}
                            </ol>

                            <p className='relative mt-8 hidden items-start gap-2 text-[11.5px] leading-relaxed text-slate-400 lg:flex'>
                                <LockKeyhole className='mt-0.5 size-3.5 shrink-0' />
                                Your password is stored encrypted. Payments go straight to the official account and are verified by the T&P Cell; this page never asks for card or bank logins.
                            </p>
                        </aside>

                        <div className='flex min-h-0 min-w-0 flex-1 flex-col'>
                            <div className='flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4 sm:px-8 sm:py-5'>
                                <div className='min-w-0'>
                                    <p className='text-[11px] font-bold uppercase tracking-[0.14em] text-indigo-600'>
                                        {step === 'done' ? 'All set' : step === 'details' ? 'Step 1 of 2' : existing ? 'Complete your payment' : 'Step 2 of 2'}
                                    </p>

                                    <h2 id='checkout-title' className='mt-1 text-xl font-bold tracking-tight text-ink sm:text-2xl'>
                                        {step === 'done' ? 'Payment submitted' : step === 'details' ? 'Create your student account' : 'Pay & unlock your portal'}
                                    </h2>
                                </div>

                                <button
                                    type='button'
                                    onClick={close}
                                    aria-label='Close checkout'
                                    disabled={submitting}
                                    className='grid size-10 shrink-0 place-items-center rounded-xl border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-ink disabled:opacity-50'
                                >
                                    <X className='size-5' />
                                </button>
                            </div>

                            {step !== 'done' && (
                                <div className='h-1 bg-slate-100'>
                                    <motion.div className='h-full bg-gradient-to-r from-indigo-600 to-emerald-500' initial={false} animate={{ width: step === 'details' ? '50%' : '100%' }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} />
                                </div>
                            )}

                            <div className='min-h-0 flex-1 overflow-y-auto'>
                                <AnimatePresence mode='wait' initial={false}>
                                    {step === 'details' && (
                                        <motion.form
                                            key='details'
                                            noValidate
                                            onSubmit={continueToPayment}
                                            initial={{ opacity: 0, x: -24 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -24 }}
                                            transition={{ duration: 0.28 }}
                                            className='space-y-5 px-5 py-6 sm:px-8'
                                        >
                                            <div className='grid gap-4 sm:grid-cols-2'>
                                                <Field label='Full name' htmlFor='checkout-name' required error={detailErrors.name} className='sm:col-span-2'>
                                                    <TextInput data-autofocus id='checkout-name' value={details.name} onChange={event => update('name', event.target.value)} autoComplete='name' placeholder='e.g. Aarav Sharma' maxLength={100} invalid={Boolean(detailErrors.name)} describedBy='checkout-name-error' />
                                                </Field>

                                                <Field label='GKV roll number' htmlFor='checkout-roll' required error={detailErrors.rollNo}>
                                                    <TextInput id='checkout-roll' value={details.rollNo} onChange={event => update('rollNo', event.target.value.toUpperCase())} placeholder='e.g. 23FET1042' autoComplete='off' maxLength={40} className='font-mono uppercase' invalid={Boolean(detailErrors.rollNo)} describedBy='checkout-roll-error' />
                                                </Field>

                                                <Field label='B.Tech year' htmlFor='checkout-year' required>
                                                    <SelectInput id='checkout-year' value={details.yearNo} onChange={event => update('yearNo', Number(event.target.value))}>
                                                        {YEARS.map(year => <option key={year.value} value={year.value}>{year.label}</option>)}
                                                    </SelectInput>
                                                </Field>

                                                <Field label='Branch' htmlFor='checkout-branch' required className='sm:col-span-2'>
                                                    <div id='checkout-branch' role='radiogroup' aria-label='Branch' className='grid grid-cols-2 gap-2 sm:grid-cols-4'>
                                                        {BRANCHES.map(branch => (
                                                            <button
                                                                key={branch.code}
                                                                type='button'
                                                                role='radio'
                                                                aria-checked={details.branch === branch.code}
                                                                onClick={() => update('branch', branch.code)}
                                                                className={cn('rounded-xl border px-3 py-2.5 text-left transition', details.branch === branch.code ? 'border-indigo-500 bg-indigo-50 ring-4 ring-indigo-500/10' : 'border-slate-200 hover:border-slate-300')}
                                                            >
                                                                <span className='block text-sm font-bold text-ink'>{branch.short}</span>
                                                                <span className='block truncate text-[11px] text-slate-500'>{branch.label.replace(/ \(.+\)$/, '')}</span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </Field>

                                                <Field label='College email' htmlFor='checkout-email' required error={detailErrors.email} className='sm:col-span-2'>
                                                    <TextInput id='checkout-email' type='email' value={details.email} onChange={event => update('email', event.target.value)} autoComplete='email' placeholder='you@gkv.ac.in' maxLength={190} invalid={Boolean(detailErrors.email)} describedBy='checkout-email-error' />
                                                </Field>

                                                <Field label='Create password' htmlFor='checkout-password' required error={detailErrors.password} className='sm:col-span-2'>
                                                    <PasswordInput id='checkout-password' value={details.password} onChange={event => update('password', event.target.value)} autoComplete='new-password' placeholder='At least 8 characters' maxLength={100} invalid={Boolean(detailErrors.password)} describedBy='checkout-password-error' />

                                                    {details.password && (
                                                        <div className='mt-2 flex items-center gap-3'>
                                                            <div className='flex flex-1 gap-1'>
                                                                {[1, 2, 3, 4].map(level => (
                                                                    <span key={level} className={cn('h-1.5 flex-1 rounded-full transition-colors', strength.score >= level ? ['bg-rose-500', 'bg-amber-500', 'bg-lime-500', 'bg-emerald-500'][strength.score - 1] : 'bg-slate-200')} />
                                                                ))}
                                                            </div>

                                                            <span className='text-xs font-semibold text-slate-500'>{strength.label}</span>
                                                        </div>
                                                    )}
                                                </Field>
                                            </div>

                                            {formError && (
                                                <p role='alert' className='flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700'>
                                                    <CircleAlert className='mt-0.5 size-4 shrink-0' />
                                                    {formError}
                                                </p>
                                            )}

                                            <div className='flex flex-col-reverse gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:items-center sm:justify-between'>
                                                <p className='text-sm text-slate-500'>
                                                    Already have an account?{' '}

                                                    <Link href='/login?role=st' className='font-semibold text-indigo-600 underline-offset-4 hover:underline'>
                                                        Sign in
                                                    </Link>
                                                </p>

                                                <motion.button
                                                    type='submit'
                                                    disabled={checking}
                                                    whileTap={{ scale: 0.98 }}
                                                    className='inline-flex items-center justify-center gap-2 rounded-xl bg-ink px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60'
                                                >
                                                    {checking ? <LoaderCircle className='size-4 animate-spin' /> : null}
                                                    Continue to payment
                                                    <ArrowRight className='size-4' />
                                                </motion.button>
                                            </div>
                                        </motion.form>
                                    )}

                                    {step === 'payment' && (
                                        <motion.form
                                            key='payment'
                                            noValidate
                                            onSubmit={completePayment}
                                            initial={{ opacity: 0, x: 24 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: 24 }}
                                            transition={{ duration: 0.28 }}
                                            className='space-y-5 px-5 py-6 sm:px-8'
                                        >
                                            <div className='flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3'>
                                                <div className='min-w-0'>
                                                    <p className='text-xs text-slate-500'>Paying as</p>
                                                    <p className='truncate text-sm font-semibold text-ink'>
                                                        <span data-mdlfy-cms={existing ? `students:${existing.id}:name` : undefined}>{payer.name}</span>
                                                        {' · '}
                                                        <span data-mdlfy-cms={existing ? `students:${existing.id}:roll_no` : undefined} className='font-mono'>{payer.rollNo}</span>
                                                    </p>
                                                </div>

                                                {!existing && (
                                                    <button type='button' onClick={() => setStep('details')} disabled={submitting} className='shrink-0 text-sm font-semibold text-indigo-600 underline-offset-4 hover:underline'>
                                                        Edit details
                                                    </button>
                                                )}
                                            </div>

                                            <fieldset>
                                                <legend className='mb-2.5 text-[13px] font-semibold text-slate-800'>Your pass</legend>

                                                <div className='grid gap-2.5 sm:grid-cols-2'>
                                                    {plans.map(option => (
                                                        <label key={option.id} className={cn('relative flex cursor-pointer items-center justify-between gap-3 rounded-2xl border p-3.5 transition', option.id === planId ? 'border-indigo-500 bg-indigo-50/60 ring-4 ring-indigo-500/10' : 'border-slate-200 hover:border-slate-300')}>
                                                            <input type='radio' name='checkout-plan' value={option.id} checked={option.id === planId} onChange={() => {
                                                                setPlanNote(null)
                                                                onPlanChange(option.id)
                                                            }} className='sr-only' />

                                                            <span className='min-w-0'>
                                                                <span className='block text-sm font-semibold text-ink'>{option.name}</span>
                                                                <span className='block text-xs text-slate-500'>{option.label} · {option.semesters}</span>
                                                            </span>

                                                            <span className='shrink-0 text-right'>
                                                                <span className='block text-base font-bold text-ink'>{formatInr(option.price)}</span>
                                                                <span className='block text-[11px] text-slate-500'>{option.cadence}</span>
                                                            </span>
                                                        </label>
                                                    ))}
                                                </div>

                                                {planNote && <p className='mt-2 text-xs font-medium text-indigo-600'>{planNote}</p>}
                                            </fieldset>

                                            <PaymentPanel
                                                idPrefix='checkout'
                                                plan={plan}
                                                settings={settings}
                                                payerNote={payer.rollNo}
                                                method={method}
                                                onMethodChange={value => {
                                                    setMethod(value)
                                                    setPaymentErrors(current => ({ ...current, reference: undefined }))
                                                }}
                                                reference={reference}
                                                onReferenceChange={value => {
                                                    setReference(value)
                                                    setPaymentErrors(current => ({ ...current, reference: undefined }))
                                                }}
                                                proof={proof}
                                                onProofChange={file => {
                                                    setProof(file)
                                                    setPaymentErrors(current => ({ ...current, proof: undefined }))
                                                }}
                                                confirmed={confirmed}
                                                onConfirmedChange={value => {
                                                    setConfirmed(value)
                                                    setPaymentErrors(current => ({ ...current, confirmed: undefined }))
                                                }}
                                                errors={paymentErrors}
                                            />

                                            {formError && (
                                                <p role='alert' className='flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700'>
                                                    <CircleAlert className='mt-0.5 size-4 shrink-0' />
                                                    {formError}
                                                </p>
                                            )}

                                            <div className='flex flex-col-reverse gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:items-center sm:justify-between'>
                                                {!existing ? (
                                                    <button type='button' onClick={() => setStep('details')} disabled={submitting} className='inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 disabled:opacity-50'>
                                                        <ArrowLeft className='size-4' />
                                                        Back
                                                    </button>
                                                ) : (
                                                    <span className='flex items-center gap-2 text-xs text-slate-500'>
                                                        <ShieldCheck className='size-4 text-emerald-500' />
                                                        Verified by the T&P Cell
                                                    </span>
                                                )}

                                                <motion.button
                                                    type='submit'
                                                    disabled={submitting || !settings}
                                                    whileTap={{ scale: 0.98 }}
                                                    className='relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-xl bg-crimson px-6 py-3.5 text-sm font-semibold text-white shadow-[0_14px_36px_-12px_rgba(217,45,32,0.8)] transition hover:bg-crimson-dark disabled:opacity-60'
                                                >
                                                    {submitting ? <LoaderCircle className='size-4 animate-spin' /> : <LockKeyhole className='size-4' />}
                                                    {submitting ? 'Submitting your payment' : 'Complete Payment & Continue to Portal'}
                                                </motion.button>
                                            </div>
                                        </motion.form>
                                    )}

                                    {step === 'done' && (
                                        <motion.div
                                            key='done'
                                            initial={{ opacity: 0, scale: 0.96 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            className='flex flex-col items-center px-6 py-14 text-center sm:py-20'
                                        >
                                            <motion.span
                                                initial={{ scale: 0.4, rotate: -20 }}
                                                animate={{ scale: 1, rotate: 0 }}
                                                transition={{ type: 'spring', stiffness: 260, damping: 16 }}
                                                className='grid size-20 place-items-center rounded-full bg-emerald-500 text-white shadow-[0_20px_50px_-16px_rgba(16,185,129,0.9)]'
                                            >
                                                <Check className='size-10' strokeWidth={3} />
                                            </motion.span>

                                            <p className='mt-6 inline-flex items-center gap-2 text-sm font-semibold text-emerald-600'>
                                                <PartyPopper className='size-4' />
                                                Welcome to GKV Tech Campus
                                            </p>

                                            <h3 className='mt-2 max-w-md text-2xl font-bold tracking-tight text-ink'>
                                                Your {formatInr(plan.price)} payment is with the T&P Cell for verification
                                            </h3>

                                            <p className='mt-3 max-w-md text-sm leading-relaxed text-slate-600'>
                                                Your account is ready. Opening your portal, where you can watch your verification status live.
                                            </p>

                                            <div className='mt-6 flex items-center gap-2 text-sm text-slate-500'>
                                                <LoaderCircle className='size-4 animate-spin' />
                                                Opening your portal
                                            </div>

                                            <Link href='/portal' className='mt-6 inline-flex items-center gap-2 rounded-xl bg-ink px-5 py-3 text-sm font-semibold text-white'>
                                                Open portal now
                                                <ArrowRight className='size-4' />
                                            </Link>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}
